import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { PermissionEnum } from '@Application/Security/Roles';
import { theme } from '@Guidelines/Theme/theme';
import SpeakerAssignmentChoices from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentChoices';
import SpeakerAssignmentFreeNameField from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentFreeNameField';
import SpeakerAssignmentMergeAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentMergeAction';
import SpeakerAssignmentSampleQuote from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentSampleQuote';
import SpeakerAssignmentStatusTag from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentStatusTag';
import SpeakerAssignmentWritableGate from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentWritableGate';
import SpeakerAssignmentUnassignAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentUnassignAction';
import speakerAssignmentSignal, {
	setSpeakerAssignmentContext,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/Signals/speakerAssignmentSignal';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useSelector } from '@verclam/synapse';
import { Sidebar } from '@verclam/ui-kit';

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	display: block;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const titleStyle = css`
	display: block;
	margin-top: 0.25rem;
`;

const contentStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

/**
 * Mounted OUTSIDE the detail screen's tree (T9, wave 3) and reachable from nowhere but the signal:
 * it can receive no prop. Every identifier the three commands need rides along in
 * `SpeakerAssignmentContext`, except `facilityId`, absent from the signal on purpose — this panel
 * only ever mounts under `RequireFacility`, so `useStrictCurrentFacility()` is safe here.
 *
 * There is no diarization-less state to render: when `speakers.list` is empty, no trigger ever
 * writes to the signal, so this panel is simply never opened (§9.5) — nothing to branch on here.
 */
const SpeakerAssignmentSidebar: React.FC = () => {
	const context = useSignalValue(speakerAssignmentSignal);
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const transcriptSpeakers = useSelector((state) => state.interviewsStore.transcriptSpeakers);
	const contacts = useSelector((state) => state.beneficiaryContactsStore.contacts);

	const subject = React.useMemo(
		() => (context === null ? null : new SubjectAddress(context.subjectTypeKey, context.subjectId)),
		[context],
	);

	const speaker = transcriptSpeakers?.findById(context?.speakerId ?? null) ?? null;

	const otherSpeakers = React.useMemo(() => {
		if (transcriptSpeakers === null || context === null) {
			return [];
		}

		return transcriptSpeakers.list.filter((entry) => entry.id !== context.speakerId);
	}, [transcriptSpeakers, context]);

	const handleHide = React.useCallback(() => {
		setSpeakerAssignmentContext(null);
	}, []);

	const header =
		speaker !== null ? (
			<React.Fragment>
				<span css={eyebrowStyle}>
					{speaker.label} · {speaker.turnCount} tours
				</span>
				<span css={titleStyle}>Qui parle ?</span>
			</React.Fragment>
		) : (
			'Qui parle ?'
		);

	return (
		<Sidebar visible={context !== null} onHide={handleHide} position="right" header={header} blockScroll>
			{context !== null && subject !== null && speaker !== null && (
				<div css={contentStyle}>
					<SpeakerAssignmentStatusTag speaker={speaker} />
					<SpeakerAssignmentSampleQuote sampleText={speaker.sampleText} />
					<SpeakerAssignmentWritableGate subject={subject}>
						<WithPermission permission={PermissionEnum.INTERVIEW_WRITE}>
							<SpeakerAssignmentChoices
								facilityId={facilityId}
								subject={subject}
								interviewId={context.interviewId}
								speaker={speaker}
								otherSpeakers={otherSpeakers}
								participants={context.participants}
								contacts={contacts}
							/>
							<SpeakerAssignmentFreeNameField
								facilityId={facilityId}
								subject={subject}
								interviewId={context.interviewId}
								speakerId={speaker.id}
							/>
							<SpeakerAssignmentUnassignAction
								facilityId={facilityId}
								subject={subject}
								interviewId={context.interviewId}
								speaker={speaker}
							/>
							<SpeakerAssignmentMergeAction
								facilityId={facilityId}
								subject={subject}
								interviewId={context.interviewId}
								source={speaker}
								otherSpeakers={otherSpeakers}
							/>
						</WithPermission>
					</SpeakerAssignmentWritableGate>
				</div>
			)}
		</Sidebar>
	);
};

export default SpeakerAssignmentSidebar;
